import * as React from 'react';

import omit from 'lodash/omit';

import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Button } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type {
  EnterpriseProgramFromSlugFragment,
  NaptimeThirdPartyOrganizationFromSlugFragment,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { EnterpriseBannerRenderer } from 'bundles/program-home/components/enterprise-home/EnterpriseBannerRenderer';
import ProgramSwitcher from 'bundles/program-home/components/multiprogram/ProgramSwitcher';
import useHasMultipleProgramProps from 'bundles/program-home/components/multiprogram/useHasMultipleProgramProps';
import type { ThirdPartyOrganizationFieldsFragment } from 'bundles/program-home/queries/__generated__/EnterpriseHomePageQuery';

import _t from 'i18n!nls/program-home';

import 'css!./__styles__/EnterpriseBanner';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

export type ThirdPartyOrganizationType =
  NaptimeThirdPartyOrganizationFromSlugFragment | ThirdPartyOrganizationFieldsFragment;

type PropsFromCaller = {
  thirdPartyOrganization: ThirdPartyOrganizationType;
  program?: EnterpriseProgramFromSlugFragment | null;
  isMultiProgram: boolean;
  isProgramDetailsView: boolean;
  shouldShowJoinButton: boolean;
  isUpswell: boolean;
  isC4cv: boolean;
  disableCTA?: boolean;
};

export type Props = PropsFromCaller;

type ButtonViewProps = {
  userId?: number;
  thirdPartyOrganization: ThirdPartyOrganizationType;
  program?: EnterpriseProgramFromSlugFragment | null;
};

type JoinButtonProps = {
  onClick: () => void;
  children: React.ReactNode;
};

export function ButtonView({ userId, thirdPartyOrganization, program }: ButtonViewProps) {
  if (!userId) {
    return null;
  }

  return (
    <ProgramSwitcher
      userId={userId}
      thirdPartyOrganizationId={thirdPartyOrganization.id}
      thirdPartyOrganizationSlug={thirdPartyOrganization.slug}
      programSlug={program?.slug}
    />
  );
}

export function JoinButton({ onClick, children }: JoinButtonProps) {
  return (
    <TrackedButton
      {...Button.defaultProps}
      className="join-button"
      variant="secondary"
      onClick={onClick}
      trackingName="banner_join_for_free"
      data-e2e="banner-join-button"
      data-testid="banner-join-button"
    >
      {children}
    </TrackedButton>
  );
}

export function EnterpriseBanner({
  thirdPartyOrganization,
  program,
  isMultiProgram,
  isProgramDetailsView,
  shouldShowJoinButton,
  isUpswell,
  isC4cv,
  disableCTA,
}: Props) {
  const router = useRouter();
  const userId = user.get().id;
  const { hasMultipleProgramsInOrg } = useHasMultipleProgramProps({
    userId,
    thirdPartyOrganizationId: thirdPartyOrganization.id,
  });

  const onJoinClicked = () => {
    if (userId) {
      // Given the current architecture, it's the easiest to reload the page to bring up the appropriate modal
      // instead of dealing with ProgramHomeModalManager directly. We might come back and revisit this.
      window.location.reload();
    } else {
      router.push({
        pathname: router.location.pathname,
        params: router.params,
        query: omit({ ...router.location.query }, 'authMode'),
        hash: buildHashWithAuthMode(getLocationHash(router.location), 'login'),
      });
    }
  };

  const showButtonView = !disableCTA && (isMultiProgram || hasMultipleProgramsInOrg);

  return (
    <EnterpriseBannerRenderer
      thirdPartyOrganization={thirdPartyOrganization}
      program={program}
      shouldShowJoinButton={shouldShowJoinButton}
      joinMessage={_t('Join')}
      showButtonView={showButtonView}
      userId={userId}
      isProgramDetailsView={isProgramDetailsView}
      onJoinClicked={onJoinClicked}
      isUpswell={isUpswell}
      isC4cv={isC4cv}
    />
  );
}

export default EnterpriseBanner;
